import * as z from 'zod'

import { useQuery } from '@tanstack/react-query'
import { PublicUser } from '~/app/api/users/public-user-schema'
import { isDev, isPreview } from '~/constants/routing'
import { invariant } from '@epic-web/invariant'
import { useSelectedTeamAndRepo } from './use-selected-team-and-repo'
import { PublicUserData } from './use-user-data'
import { useSelectedBranchOrCommitQuery } from '~/lib/commits/use-selected-branch-or-commit'
import {
  SYSTEM_AUTHORS,
  getPublicUserFromSystemAuthor,
} from '~/lib/system-authors'
import uniqBy from 'lodash/uniqBy'
import { isBranch } from '@basehub/data/replicache/index'

const teamMembersAndRepoGuestsSchema = z.object({
  teamMembers: z.array(PublicUser),
  repoGuests: z.array(PublicUser),
  branchOrCommitAuthor: PublicUser.nullish(),
})

/**
 * fetches user data from api
 * for "me", it'll get it fresh from useClerk.
 * if `null`, will use the current authenticated user
 */
export function useRepoMembers() {
  const teamAndRepo = useSelectedTeamAndRepo().data
  const teamId = teamAndRepo?.team?.id
  const repoId = teamAndRepo?.repo?.id
  const selectedBranchOrCommit = useSelectedBranchOrCommitQuery().data

  const query = useQuery<PublicUserData[] | null>({
    queryKey: [
      'repo-and-branch-member-list',
      teamId,
      repoId,
      selectedBranchOrCommit?.id,
    ],
    queryFn: async () => {
      invariant(teamId, 'not enabled')
      invariant(repoId, 'not enabled')
      invariant(selectedBranchOrCommit?.id, 'not enabled')

      const res = await fetch(
        `/api/users?team-id=${teamId}&repo-id=${repoId}&ref=${selectedBranchOrCommit.id}&root-id=${isBranch(selectedBranchOrCommit) ? selectedBranchOrCommit.headCommit?.rootBlockId : selectedBranchOrCommit.rootBlockId}`
      )
      const data = await res.json()
      const parsedUsers = teamMembersAndRepoGuestsSchema.safeParse(data)

      if (!parsedUsers.success) {
        if (isPreview || isDev) return null
        throw new Error(`Invalid response data: ${parsedUsers.error.message}`)
      }
      if (!parsedUsers.data) {
        throw new Error('No data')
      }

      const dedupedMembers = uniqBy(
        !!parsedUsers.data.branchOrCommitAuthor
          ? [
              ...parsedUsers.data.repoGuests,
              ...parsedUsers.data.teamMembers,
              parsedUsers.data.branchOrCommitAuthor,
              getPublicUserFromSystemAuthor(SYSTEM_AUTHORS.START),
            ]
          : [
              ...parsedUsers.data.repoGuests,
              ...parsedUsers.data.teamMembers,
              getPublicUserFromSystemAuthor(SYSTEM_AUTHORS.START),
            ],
        (user) => user.id
      )

      return dedupedMembers.sort((a, b) =>
        (a.firstName ?? a.lastName ?? a.username ?? '').localeCompare(
          b.firstName ?? b.lastName ?? b.username ?? ''
        )
      )
    },
    enabled: !!teamId && !!repoId && !!selectedBranchOrCommit?.id,
    initialData: [
      getPublicUserFromSystemAuthor(SYSTEM_AUTHORS.START),
    ] satisfies PublicUserData[],
  })

  return query
}
